// E25-t2 — THE KNOWLEDGE BASE WORKSPACE, per DocujointKb.dc.html (2026-08-06).
//
// The design reads /:org/:kb as a HUB with a hero band and a panel grid:
//
//   HERO      the KB's identity (dot · name · primary-format badge), its mono
//             facts (clone origin · files · storage), the referee's health
//             chip with the acceptance rate, LAST PUSH (commit · day), and
//             the two acts: Agent chat (tier-gated) and Open <primary
//             dashboard>.
//   PANELS    Dashboards (the manifest's compositions as rows — title,
//             Default badge, the Report chip where the format owns the
//             surface, format · rendered commit · day · views · the report's
//             next bake when an armed schedule runs the surface (E33), Open),
//             Reports (name · schedule · next due · state badge, All → the
//             org page that owns consent), Usage (thirty days of writes as
//             the design's chart with WRITES / FILES / STORAGE / REJECTED
//             beneath — `refused` is the activity feed's own number), and
//             Agent (tier-gated: absent, never teased).
//
// EVERY PANEL DEGRADES ALONE to the transport's own sentence — the design
// draws that state per panel and this page honours it per panel.
//
// WHAT THE DESIGN DRAWS THAT THIS PAGE DOES NOT, on the record:
//   - the per-commit RECENT WRITES strip — no endpoint answers a commit feed
//     (activity is per-day counts); it arrives with its machinery.
//   - "Publish composition" — composing is the tier-hidden agent surface.
//   - the Agent panel's session stats — nothing stores agent session
//     summaries; the panel offers the door and claims nothing else.
import { useEffect, useState } from 'react';

import { Badge } from '@astryxdesign/core/Badge';
import { Button } from '@astryxdesign/core/Button';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Heading } from '@astryxdesign/core/Heading';
import { Link } from '@astryxdesign/core/Link';
import { MoreMenu } from '@astryxdesign/core/MoreMenu';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { Stack } from '@astryxdesign/core/Stack';
import { StatusDot } from '@astryxdesign/core/StatusDot';
import { Text } from '@astryxdesign/core/Text';
import { Chart, area, line } from '@astryxdesign/charts';

import { nextDueAt, scheduleLabel } from '../../../platform/src/report-schedule';
import type { DashboardsClient, KbDashboards } from '../api/dashboards';
import type { CorpusTree, FilesClient } from '../api/files';
import type { ReportRow, ReportsClient } from '../api/reports';
import type { ActivityDay, KbRow, KbsClient, KbUsage } from '../api/kbs';
import { Eyebrow } from '../components/Eyebrow';
import { identityColor } from '../identity';
import { humanBytes, humanCount, isoDay, relShort } from '../format/units';
import { documentsOf } from './KbFiles';

import './kb-workspace.css';

export interface KbWorkspaceProps {
  org: string;
  kb: string;
  dashboards: DashboardsClient;
  reports: ReportsClient;
  kbs: KbsClient;
  /** E37-t6 — the corpus snapshot's manifest, for the Files panel's facts */
  files: FilesClient;
  /** E14-t5 — the tier gate, passed by the container like everywhere else */
  showAgentSurfaces?: boolean;
}

/** One panel's read: the value, or the transport's own sentence. */
type Read<T> =
  | { state: 'reading' }
  | { state: 'ready'; value: T }
  | { state: 'refused'; reason: string };

function useRead<T>(
  load: () => Promise<T>,
  deps: ReadonlyArray<unknown>,
): { read: Read<T>; retry: () => void } {
  const [read, setRead] = useState<Read<T>>({ state: 'reading' });
  // a refusal is retriable in place (2026-08-07 audit) — the attempt counter
  // re-arms the same effect, so a retry is the same read, not a new one
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setRead({ state: 'reading' });
    void load()
      .then((value) => {
        if (live) setRead({ state: 'ready', value });
      })
      .catch((error: Error) => {
        if (live) setRead({ state: 'refused', reason: error.message });
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt]);
  return { read, retry: () => setAttempt((a) => a + 1) };
}

const shortOid = (oid: string): string => oid.slice(0, 7);

/** The panel's shared chrome: header row, divided body, three shared states.
 *  `meta` sits at the title's baseline (the design's header counts); `aside`
 *  stays the right-edge act. */
function Panel({
  title,
  meta,
  aside,
  span,
  children,
}: {
  title: string;
  meta?: React.ReactNode;
  aside?: React.ReactNode;
  span: 5 | 7 | 12;
  children: React.ReactNode;
}) {
  return (
    <section className={`dj-panel dj-panel--${span}`} aria-label={title}>
      <div className="dj-panel-head">
        <div className="dj-panel-title">
          <Heading level={2} type="display-3">
            {title}
          </Heading>
          {meta}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** Mono facts separated by the design's dimmed middots — presentational
 *  separators, so they are aria-hidden spans, not part of the string. */
function Facts({ facts }: { facts: ReadonlyArray<string | null | false | undefined> }) {
  const present = facts.filter((fact): fact is string => Boolean(fact));
  return (
    <>
      {present.map((fact, i) => (
        <span key={i}>
          {i > 0 ? (
            <span className="dj-sep" aria-hidden>
              {' · '}
            </span>
          ) : null}
          {fact}
        </span>
      ))}
    </>
  );
}

function PanelReading({ lines = 2 }: { lines?: number }) {
  return (
    <div className="dj-panel-body" aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Stack key={i} direction="vertical" gap={1}>
          <Skeleton width={`${44 + ((i * 31) % 26)}%`} height="12px" index={i} />
          <Skeleton width="62%" height="10px" index={i} />
        </Stack>
      ))}
    </div>
  );
}

function PanelRefused({
  what,
  reason,
  onRetry,
}: {
  what: string;
  reason: string;
  onRetry?: () => void;
}) {
  return (
    <div className="dj-panel-body">
      <Stack direction="horizontal" gap={2} vAlign="center">
        <StatusDot variant="error" label={`${what} refused`} />
        <Text type="label" weight="semibold">
          {what} refused
        </Text>
      </Stack>
      {/* the transport's own sentence, mono — a refusal is a machine fact */}
      <Text type="code" color="secondary">
        {reason}
      </Text>
      <Stack direction="horizontal" gap={2}>
        {onRetry ? <Button label="Retry" variant="secondary" size="sm" onClick={onRetry} /> : null}
        <Button
          label="Copy error"
          variant="ghost"
          size="sm"
          onClick={() => void navigator.clipboard?.writeText(reason)}
        />
      </Stack>
    </div>
  );
}

export function KbWorkspace({
  org,
  kb,
  dashboards,
  reports,
  kbs,
  files,
  showAgentSurfaces = true,
}: KbWorkspaceProps) {
  const { read: boards, retry: retryBoards } = useRead<KbDashboards>(
    () => dashboards.listDashboards(org, kb),
    [org, kb],
  );
  const { read: declared, retry: retryDeclared } = useRead<{
    commit: string | null;
    reports: ReportRow[];
  }>(() => reports.listReports(org, kb), [org, kb]);
  const { read: usage } = useRead<KbUsage>(() => kbs.loadUsage(org, kb), [org, kb]);
  const { read: activity, retry: retryActivity } = useRead<ActivityDay[]>(
    () => kbs.loadActivity(org, kb),
    [org, kb],
  );
  // E37-t6 — the corpus snapshot's manifest: the Files panel's own read, so a
  // KB that has never been pushed to costs that panel a sentence and nothing else
  const { read: corpus, retry: retryCorpus } = useRead<CorpusTree>(
    () => files.loadTree(org, kb),
    [org, kb],
  );
  // the hero's registry facts — the same row the overview's shelf reads
  const { read: registry } = useRead<KbRow | null>(
    () => kbs.listKbs().then((rows) => rows.find((row) => row.org === org && row.name === kb) ?? null),
    [org, kb],
  );

  const row = registry.state === 'ready' ? registry.value : null;
  const verdict = row?.verdict ?? null;
  // one identity color for the KB: the hero dot and the usage chart agree
  const accent = identityColor(kb);

  const primary =
    boards.state === 'ready'
      ? boards.value.formats.find((format) => format.primary) ?? boards.value.formats[0] ?? null
      : null;
  const primaryTitle = primary?.compositions[0]?.title || primary?.format || null;

  // E33-t4 — WHEN THE REPORT SURFACE NEXT BAKES, per format. An E22 schedule
  // may name the surface as what it runs (t3: `runs: surface`, targeting a
  // format by name or — when the envelope names none — the KB's primary, which
  // the DO resolves at fire time and this panel mirrors with the manifest's
  // own primary flag). Only an ARMED schedule has a next run; the earliest
  // wins when several target one format. Answered from the SAME reports read
  // the Reports panel makes, so the per-panel degrade doctrine holds by
  // construction: a refused reports read costs this fact its presence, never
  // the Dashboards panel its rows.
  const surfaceNextRun = (formatName: string, isPrimary: boolean): string | null => {
    if (declared.state !== 'ready') return null;
    const nowIso = new Date().toISOString();
    let earliest: string | null = null;
    for (const report of declared.value.reports) {
      if (report.state !== 'armed' || report.schedule?.runs !== 'surface') continue;
      const target = report.schedule.surfaceFormat ?? null;
      if (target === null ? !isPrimary : target !== formatName) continue;
      const due = nextDueAt(report.schedule, nowIso);
      if (earliest === null || due < earliest) earliest = due;
    }
    return earliest;
  };

  const accepted =
    activity.state === 'ready' ? activity.value.reduce((s, d) => s + d.accepted, 0) : null;
  const refused =
    activity.state === 'ready' ? activity.value.reduce((s, d) => s + d.refused, 0) : null;
  const rate =
    accepted !== null && refused !== null && accepted + refused > 0
      ? `${((accepted / (accepted + refused)) * 100).toFixed(1)}% accepted · 30d`
      : null;

  const chartDays =
    activity.state === 'ready'
      ? (() => {
          const byDay = new Map(activity.value.map((d) => [d.day, d.accepted]));
          return Array.from({ length: 30 }, (_, i) => {
            const day = new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000)
              .toISOString()
              .slice(0, 10);
            return { day, writes: byDay.get(day) ?? 0 };
          });
        })()
      : null;

  return (
    <div data-page="kb-workspace" className="dj-workspace">
      {/* ── THE HERO BAND ─────────────────────────────────────────────── */}
      <header className="dj-workspace-hero">
        <Stack direction="horizontal" gap={4} vAlign="start" hAlign="between" wrap="wrap">
          <Stack direction="vertical" gap={1}>
            <Stack direction="horizontal" gap={2} vAlign="center" wrap="wrap">
              <span
                aria-hidden="true"
                className="dj-workspace-dot"
                style={{ background: accent }}
              />
              <Heading level={1} type="display-3">
                {kb}
              </Heading>
              {primary ? <Badge label={primary.format} /> : null}
            </Stack>
            {/* the mono fact line — the host's own strings, dashes for the
                counts a read has not answered */}
            <Text type="code" color="secondary">
              <Facts
                facts={[
                  row?.gitUrl ?? org,
                  usage.state === 'ready' ? `${humanCount(usage.value.files)} files` : null,
                  usage.state === 'ready' ? humanBytes(usage.value.bytes) : null,
                ]}
              />
            </Text>
          </Stack>
          <Stack direction="horizontal" gap={2} vAlign="center">
            {showAgentSurfaces ? (
              <Button label="Agent chat" variant="ghost" size="sm" href={`/${org}/${kb}/agent`} />
            ) : null}
            {/* E33-t4 — the hero presents the surface FAMILY: when the primary
                format owns a report surface, its door stands beside the
                dashboard's, ghost to the dashboard's primary. The url is the
                catalog's own (the reserved /report segment), never assembled
                here — the same rule every url on this page obeys. */}
            {primary?.report ? (
              <Button label="Open report" variant="ghost" size="sm" href={primary.report.url} />
            ) : null}
            {primaryTitle ? (
              <Button
                label={`Open ${primaryTitle}`}
                variant="primary"
                size="sm"
                icon={
                  // the design's arrow glyph on the hero act — inline so the
                  // page owes no icon package
                  <svg
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.6}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M6 12h12" />
                    <path d="M14 7l5 5-5 5" />
                  </svg>
                }
                href={`/${org}/${kb}/dashboards/${encodeURIComponent(primary?.format ?? '')}`}
              />
            ) : null}
          </Stack>
        </Stack>

        {/* the health chip row: the verdict as the referee said it, and the
            last push it was said about */}
        <Stack direction="horizontal" gap={4} vAlign="center" wrap="wrap">
          {verdict ? (
            <span className="dj-workspace-chip">
              <StatusDot
                variant={
                  verdict.status === 'green'
                    ? 'success'
                    : verdict.status === 'red'
                      ? 'error'
                      : 'neutral'
                }
                label={`Referee ${verdict.status}`}
                tooltip={verdict.checkedAt ? `Checked ${relShort(verdict.checkedAt)}` : undefined}
              />
              <Text type="label" weight="semibold">
                Referee {verdict.status}
              </Text>
              {rate ? (
                <Text type="code" color="secondary">
                  {rate}
                </Text>
              ) : null}
            </span>
          ) : registry.state === 'refused' ? (
            /* the refused chip carries a title over the transport's sentence,
               dot top-aligned against the two lines */
            <span className="dj-workspace-chip dj-workspace-chip--refused">
              <StatusDot variant="error" label="Verdict unavailable" />
              <Stack direction="vertical" gap={1}>
                <Text type="label" weight="semibold">
                  Referee verdict unavailable
                </Text>
                <Text type="code" color="secondary">
                  {registry.reason}
                </Text>
              </Stack>
            </span>
          ) : registry.state === 'ready' ? (
            <span className="dj-workspace-chip">
              <StatusDot variant="neutral" label="No verdict yet" />
              <Text type="label" weight="semibold">
                No verdict yet
              </Text>
            </span>
          ) : registry.state === 'reading' ? (
            /* the chip row holds its shape while the registry answers */
            <>
              <Skeleton width="240px" height="26px" radius={3} index={1} />
              <Skeleton width="180px" height="12px" radius={2} index={2} />
            </>
          ) : null}
          {verdict?.oid ? (
            <Stack direction="horizontal" gap={2} vAlign="center">
              <Eyebrow>Last push</Eyebrow>
              <Text type="code" color="secondary">
                {shortOid(verdict.oid)}
                {verdict.checkedAt ? ` · ${relShort(verdict.checkedAt)}` : ''}
              </Text>
            </Stack>
          ) : null}
        </Stack>
      </header>

      {/* ── THE PANEL GRID ────────────────────────────────────────────── */}
      <div className="dj-workspace-grid">
        <Panel
          title="Dashboards"
          span={7}
          meta={
            boards.state === 'ready' ? (
              <Text type="code" color="secondary">
                {humanCount(
                  boards.value.formats.reduce((n, f) => n + f.compositions.length, 0),
                )}{' '}
                published
              </Text>
            ) : undefined
          }
        >
          {boards.state === 'reading' ? (
            <PanelReading lines={3} />
          ) : boards.state === 'refused' ? (
            <PanelRefused what="Dashboard index" reason={boards.reason} onRetry={retryBoards} />
          ) : boards.value.formats.length === 0 ? (
            <div className="dj-panel-body">
              {/* "Publish composition" stays out — composing is the tier-hidden
                  agent surface (machinery gap, header comment) */}
              <EmptyState
                title="No dashboards published"
                description="A dashboard is declared in the KB's own dashboard.yaml and rendered on a green push of the default branch."
                isCompact
                headingLevel={3}
                actions={
                  <Text type="supporting" color="secondary">
                    Dashboards are published from the CLI.
                  </Text>
                }
              />
            </div>
          ) : (
            <div className="dj-panel-rows">
              {boards.value.formats.flatMap((format) => {
                // E33-t4 — the next bake sits beside the Report chip it is
                // about, so it only prints where the format OWNS the surface
                const reportNext = format.report
                  ? surfaceNextRun(format.format, format.primary)
                  : null;
                return format.compositions.map((composition) => (
                  <div key={`${format.format}/${composition.id}`} className="dj-panel-row">
                    <Stack direction="vertical" gap={1}>
                      <Stack direction="horizontal" gap={2} vAlign="center">
                        <Link
                          href={`/${org}/${kb}/dashboards/${encodeURIComponent(format.format)}`}
                        >
                          {composition.title || format.format}
                        </Link>
                        {format.primary ? <Badge label="Default" /> : null}
                        {/* E32-t5 — the surface family, at panel altitude: the
                            format owns a report, so its row says so with the
                            one act the surface has. The url is the catalog's
                            (the reserved /report segment); the full family
                            reading lives on the Dashboards page. */}
                        {format.report ? <Link href={format.report.url}>Report</Link> : null}
                      </Stack>
                      <Text type="code" color="secondary">
                        <Facts
                          facts={[
                            format.format,
                            composition.commit && `rendered ${shortOid(composition.commit)}`,
                            composition.renderedAt && relShort(composition.renderedAt),
                            // E32-t5 — the library's size as a mono fact; the
                            // views themselves (names, files, anchors) are the
                            // Dashboards page's business, not this panel's
                            format.storedViews.length > 0 &&
                              `${format.storedViews.length} ${
                                format.storedViews.length === 1 ? 'view' : 'views'
                              }`,
                            // E33-t4 — when an armed schedule runs this
                            // format's report surface (t3), the panel says
                            // when it next bakes; the ISO day, for the same
                            // reason the Reports panel's next-due is one
                            reportNext && `report next ${isoDay(reportNext)}`,
                          ]}
                        />
                      </Text>
                    </Stack>
                    <Stack direction="horizontal" gap={1} vAlign="center">
                      <Button
                        label="Open"
                        variant="secondary"
                        size="sm"
                        href={`/${org}/${kb}/dashboards/${encodeURIComponent(format.format)}`}
                      />
                      {/* "Make default" stays out — no endpoint reorders the
                          manifest's primary (machinery gap) */}
                      <MoreMenu
                        label={`${composition.title || format.format} actions`}
                        variant="ghost"
                        size="sm"
                        items={[
                          {
                            label: 'Open',
                            onClick: () => {
                              window.location.href = `/${org}/${kb}/dashboards/${encodeURIComponent(format.format)}`;
                            },
                          },
                          ...(composition.commit
                            ? [
                                {
                                  label: 'Copy render commit',
                                  onClick: () => {
                                    void navigator.clipboard?.writeText(composition.commit ?? '');
                                  },
                                },
                              ]
                            : []),
                        ]}
                      />
                    </Stack>
                  </div>
                ));
              })}
            </div>
          )}
        </Panel>

        <Panel
          title="Reports"
          span={5}
          meta={
            declared.state === 'ready' ? (
              <Text type="code" color="secondary">
                {humanCount(declared.value.reports.length)} declared
              </Text>
            ) : undefined
          }
          aside={<Link href={`/${org}/reports`}>All</Link>}
        >
          {declared.state === 'reading' ? (
            <PanelReading lines={2} />
          ) : declared.state === 'refused' ? (
            /* "Request access" stays out — nothing grants access (machinery gap) */
            <PanelRefused what="Reports" reason={declared.reason} onRetry={retryDeclared} />
          ) : declared.value.reports.length === 0 ? (
            <div className="dj-panel-body">
              <Text type="label" weight="semibold">
                No reports declared
              </Text>
              <Text type="supporting" color="secondary">
                A report is a `type: report` document in this KB; declare one and it appears here
                with its armed state.
              </Text>
            </div>
          ) : (
            <div className="dj-panel-rows">
              {declared.value.reports.map((report) => (
                <div key={report.uuid} className="dj-panel-row">
                  <Stack direction="vertical" gap={1}>
                    <Text type="label" weight="semibold">
                      {report.title}
                    </Text>
                    {report.schedule ? (
                      <Text type="code" color="secondary">
                        <Facts
                          facts={[
                            scheduleLabel(report.schedule),
                            // E33-t4 — a schedule that runs the report SURFACE
                            // (t3's runnable) says so, naming its target the
                            // envelope's way: a format by name, or 'primary'
                            // (resolved by the DO at fire time, so this panel
                            // repeats the word rather than guessing the name)
                            report.schedule.runs === 'surface' &&
                              `runs the ${report.schedule.surfaceFormat ?? 'primary'} report surface`,
                            // next-due stays the ISO day: relShort's grammar is
                            // "ago" and collapses any FUTURE instant to "now"
                            report.state === 'armed' &&
                              `next ${isoDay(nextDueAt(report.schedule, new Date().toISOString()))}`,
                          ]}
                        />
                      </Text>
                    ) : null}
                  </Stack>
                  <Badge
                    label={report.state}
                    variant={report.state === 'armed' ? 'success' : undefined}
                  />
                </div>
              ))}
              <div className="dj-panel-body">
                <Text type="supporting" color="secondary">
                  Consent lives on the <Link href={`/${org}/reports`}>organization&rsquo;s Reports
                  page</Link> — arming a schedule is the org&rsquo;s act.
                </Text>
              </div>
            </div>
          )}
        </Panel>

        <Panel title="Usage" span={5} aside={<Eyebrow>Last 30 days</Eyebrow>}>
          {activity.state === 'reading' || usage.state === 'reading' ? (
            <PanelReading lines={2} />
          ) : activity.state === 'refused' ? (
            <PanelRefused what="Write ledger" reason={activity.reason} onRetry={retryActivity} />
          ) : (
            <div className="dj-panel-body">
              {chartDays ? (
                <div className="dj-workspace-chart" aria-hidden="true">
                  <Chart
                    data={chartDays}
                    xKey="day"
                    height={72}
                    margin={{ top: 6, right: 2, bottom: 2, left: 2 }}
                    yBaseline="data"
                    series={[
                      area('writes', { color: accent, gradient: true }),
                      line('writes', { color: accent, strokeWidth: 1.5 }),
                    ]}
                  />
                </div>
              ) : null}
              <div className="dj-workspace-stats">
                <div>
                  <span className="dj-workspace-figure">
                    {accepted === null ? '—' : humanCount(accepted)}
                  </span>
                  <Eyebrow>Writes</Eyebrow>
                </div>
                <div>
                  <span className="dj-workspace-figure">
                    {usage.state === 'ready' ? humanCount(usage.value.files) : '—'}
                  </span>
                  <Eyebrow>Files</Eyebrow>
                </div>
                <div>
                  <span className="dj-workspace-figure">
                    {usage.state === 'ready' ? humanBytes(usage.value.bytes) : '—'}
                  </span>
                  <Eyebrow>Storage</Eyebrow>
                </div>
                <div>
                  <span className="dj-workspace-figure">
                    {refused === null ? '—' : humanCount(refused)}
                  </span>
                  <Eyebrow>Rejected</Eyebrow>
                </div>
              </div>
              {usage.state === 'refused' ? (
                <Text type="code" color="secondary">
                  {usage.reason}
                </Text>
              ) : null}
            </div>
          )}
        </Panel>

        {/* Agent — tier-gated: absent, never teased. The design's session
            stats arrive with the machinery that would store them. */}
        {showAgentSurfaces ? (
          <Panel
            title="Agent"
            span={7}
            aside={
              <Button label="Open chat" variant="secondary" size="sm" href={`/${org}/${kb}/agent`} />
            }
          >
            <div className="dj-panel-body">
              <Text type="body" color="secondary">
                Ask the corpus, draft views, run refereed commands — every agent write goes
                through the referee like any other write.
              </Text>
            </div>
          </Panel>
        ) : null}

        {/* E37-t6 — Files: the corpus snapshot's facts, and the door to the
            tree. The panel reads the manifest (one Worker-side R2 read); the
            browsing itself is the /files page's. A KB that has never had an
            accepted push degrades to the door's own 404 sentence, per panel.
            Last and full-width, so the established 7/5 rows above keep their
            pinned shape whether or not the tier draws the Agent panel. */}
        <Panel
          title="Files"
          span={12}
          meta={
            corpus.state === 'ready' ? (
              <Text type="code" color="secondary">
                {humanCount(documentsOf(corpus.value.files).length)} documents
              </Text>
            ) : undefined
          }
          aside={
            corpus.state === 'ready' ? (
              <Button label="Browse" variant="secondary" size="sm" href={`/${org}/${kb}/files`} />
            ) : undefined
          }
        >
          {corpus.state === 'reading' ? (
            <PanelReading lines={1} />
          ) : corpus.state === 'refused' ? (
            <PanelRefused what="Corpus snapshot" reason={corpus.reason} onRetry={retryCorpus} />
          ) : (
            <div className="dj-panel-body">
              <Text type="code" color="secondary">
                <Facts
                  facts={[
                    `checkout ${shortOid(corpus.value.commit)}`,
                    corpus.value.branch && `branch ${corpus.value.branch}`,
                    corpus.value.updatedAt && relShort(corpus.value.updatedAt),
                    `${humanCount(corpus.value.files.length)} files in the snapshot`,
                  ]}
                />
              </Text>
              <Text type="supporting" color="secondary">
                The vault as checked in, read as a tree — documents render as Markdown. Reading
                only: changing the corpus stays a push or a command.
              </Text>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
